/**
 * Hook to fetch and manage widget visibility configuration from Supabase
 */

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../utils/supabaseClient';
import { WidgetConfig, WidgetKey, DEFAULT_CALCULATOR_WIDGETS } from '../types/widgets';

interface UseWidgetConfigResult {
  widgets: WidgetConfig[];
  visibleWidgets: WidgetConfig[];
  isLoading: boolean;
  error: Error | null;
  isWidgetVisible: (key: WidgetKey) => boolean;
  refreshWidgets: () => Promise<void>;
}

/**
 * Fetches widget configuration from database and provides visibility helpers
 */
export function useWidgetConfig(tabName: string = 'calculator'): UseWidgetConfigResult {
  const [widgets, setWidgets] = useState<WidgetConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchWidgets = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Check localStorage cache (1 hour)
      const cacheKey = `widget_config_${tabName}_v2`; // v2 to force refresh for new widgets
      const cachedStr = localStorage.getItem(cacheKey);
      let data: any[] | null = null;
      
      if (cachedStr) {
        try {
          const parsed = JSON.parse(cachedStr);
          const age = Date.now() - (parsed.timestamp || 0);
          if (age < 60 * 60 * 1000) { // 1 hour
            data = parsed.data;
          }
        } catch (e) { }
      }
      
      if (!data) {
        const { data: fetchedData, error: fetchError } = await supabase
          .from('widget_config')
          .select('*')
          .eq('tab_name', tabName)
          .order('display_order', { ascending: true });
        
        if (fetchError) throw fetchError;
        data = fetchedData;
        
        if (data) {
          localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
        }
      }

      if (data && data.length > 0) {
        console.log('[useWidgetConfig] Loaded widgets from DB:');
        data.forEach(w => {
          console.log(`  ${w.display_order}: ${w.widget_key} (${w.display_name}) - visible: ${w.is_visible}`);
        });
        setWidgets(data as WidgetConfig[]);
      } else {
        // Fallback to defaults if no DB config
        const defaultConfigs = DEFAULT_CALCULATOR_WIDGETS
          .filter(w => w.tabName === tabName)
          .map((w, idx) => ({
            id: `default-${w.key}`,
            widget_key: w.key,
            display_name: w.displayName,
            tab_name: w.tabName,
            display_order: w.defaultOrder,
            is_visible: w.defaultVisible,
            description: w.description,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }));
        setWidgets(defaultConfigs);
      }
    } catch (err) {
      console.error('[useWidgetConfig] Error fetching widgets:', err);
      setError(err as Error);
      // Fallback to defaults on error
      const defaultConfigs = DEFAULT_CALCULATOR_WIDGETS
        .filter(w => w.tabName === tabName)
        .map((w, idx) => ({
          id: `default-${w.key}`,
          widget_key: w.key,
          display_name: w.displayName,
          tab_name: w.tabName,
          display_order: w.defaultOrder,
          is_visible: w.defaultVisible,
          description: w.description,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }));
      setWidgets(defaultConfigs);
    } finally {
      setIsLoading(false);
    }
  }, [tabName]);

  useEffect(() => {
    fetchWidgets();
  }, [fetchWidgets]);

  const visibleWidgets = widgets.filter(w => w.is_visible);

  const isWidgetVisible = useCallback(
    (key: WidgetKey): boolean => {
      const widget = widgets.find(w => w.widget_key === key);
      return widget ? widget.is_visible : true; // Default to visible if not found
    },
    [widgets]
  );

  return {
    widgets,
    visibleWidgets,
    isLoading,
    error,
    isWidgetVisible,
    refreshWidgets: fetchWidgets,
  };
}
